import React, { useCallback } from 'react';

import ChatIcon from '@atlaskit/icon/core/ai-chat';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { useRovoNavigation } from '@atlassian/conversation-assistant';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useIntl } from 'react-intl';

import { type CommonSideNavItemProps } from '@townsquare/griffin-navigation/types';
import { useRouterActions } from '@townsquare/router/primitives/actions';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { matchRoute } from '@townsquare/router/primitives/match-route';
import { generatePath } from '@townsquare/router/primitives/path';
import { useRouter } from '@townsquare/router/useRouter';
import { chatRoute } from '@townsquare/routes/chat';
import { useAssistanceServiceParams } from '@townsquare/rovo/hooks';
import { fg } from '@townsquare/stat-sig/gate';

export const RovoChatNavItem = (props: CommonSideNavItemProps): React.JSX.Element => {
	const { onClick, item } = props;
	const intl = useIntl();
	const { pushTo } = useRouterActions();
	const [routerStore] = useRouter();
	const { activeMenu } = useUIStore();
	const routeDependencies = useRouteDependencies();

	const assistanceServiceParams = useAssistanceServiceParams({
		experienceId: 'ai-mate',
		touchpointSource: 'townsquare-sidenav',
	});
	const { handleNewChatClick } = useRovoNavigation({
		assistanceServiceParams,
		isTasksEnabled: fg('rovo_chat_enable_tasks'),
	});

	const isOnChatRoute = !!matchRoute([chatRoute], routerStore.location.pathname, undefined);
	const isSelected = isOnChatRoute && !(fg('rovo_chat_enable_tasks') && activeMenu === 'tasks');

	const chatHref = generatePath(chatRoute, routeDependencies);

	const onChatClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			pushTo(chatRoute);
			handleNewChatClick(true);
			onClick(item, e);
		},
		[pushTo, handleNewChatClick, item, onClick],
	);

	return (
		<LinkMenuItem
			href={chatHref}
			elemBefore={<ChatIcon label="" color="currentColor" />}
			onClick={onChatClick}
			isSelected={isSelected}
			testId="side-nav-chat"
		>
			{intl.formatMessage({
				id: 'townsquare.atlassian-home.side-nav.chat',
				description: 'Side nav button to open chat page',
				defaultMessage: 'Chat',
			})}
		</LinkMenuItem>
	);
};
